import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Card, CardContent, FormControl, InputLabel, Select, MenuItem, Grid, Paper, Divider, Alert, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { TrendingUp as TrendingUpIcon, AccountBalance as AccountBalanceIcon, Assessment as AssessmentIcon } from "@mui/icons-material";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";

const EmployeeTaxSummary = () => {
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const [financialYear, setFinancialYear] = useState("");
    const [taxSummary, setTaxSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const currentYear = new Date().getFullYear();
        const currentMonth = new Date().getMonth();
        const defaultFY = currentMonth >= 3 ? `${currentYear}-${currentYear + 1}` : `${currentYear - 1}-${currentYear}`;
        setFinancialYear(defaultFY);
    }, []);

    const fetchTaxSummary = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response = await getData(API_ENDPOINTS.getTaxSummaryByFinancialYear(userId, financialYear));

            if (response.statusCode === 200) {
                setTaxSummary(response.result);
            } else {
                setError(response.message || "Failed to fetch tax summary");
                setTaxSummary(null);
            }
        } catch (error) {
            setError("Error loading tax summary");
            setTaxSummary(null);
        } finally {
            setLoading(false);
        }
    }, [userId, financialYear]);

    useEffect(() => {
        if (financialYear) {
            fetchTaxSummary();
        }
    }, [financialYear, fetchTaxSummary]);

    const formatCurrency = (value) => {
        return `₹${(value || 0).toLocaleString()}`;
    };

    return (
        <Box>
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, md: 12 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Financial Year</InputLabel>
                                <Select value={financialYear} onChange={(e) => setFinancialYear(e.target.value)} label="Financial Year" >
                                    <MenuItem value="2023-2024">2023-2024</MenuItem>
                                    <MenuItem value="2024-2025">2024-2025</MenuItem>
                                    <MenuItem value="2025-2026">2025-2026</MenuItem>
                                    <MenuItem value="2026-2027">2026-2027</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 5 }}>
                    <CircularProgress />
                </Box>
            ) : taxSummary ? (
                <>
                    {/* Tax summary Cards */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: "100%", bgcolor: "primary.light", color: "primary.contrastText" }}>
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                        <AccountBalanceIcon sx={{ mr: 1, fontSize: 32 }} />
                                        <Typography variant="h6">Gross Annual Salary</Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        {formatCurrency(taxSummary.grossAnnual)}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ mt: 1, opacity: 0.9 }}>
                                        Before Exemptions & Deductions
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card sx={{ height: "100%", bgcolor: "secondary.light", color: "secondary.contrastText" }}>
                                <CardContent>
                                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                        <AssessmentIcon sx={{ mr: 1, fontSize: 32 }} />
                                        <Typography variant="h6">Net Tax Payable ({taxSummary.regime})</Typography>
                                    </Box>
                                    <Typography variant="h3" fontWeight="bold">
                                        {formatCurrency(taxSummary.annualTax)}
                                    </Typography>
                                    <Typography variant="subtitle2" sx={{ mt: 1, opacity: 0.9 }}>
                                        Estimated Annual Tax Liability
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Taxable Income Card */}
                    <Card sx={{ mb: 3, bgcolor: "info.light" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                                <TrendingUpIcon sx={{ mr: 2, fontSize: 40, color: "info.dark" }} />
                                <Box>
                                    <Typography variant="h6" color="text.secondary">
                                        Total Taxable Income
                                    </Typography>
                                    <Typography variant="h4" fontWeight="bold">
                                        {formatCurrency(taxSummary.taxableIncome)}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Declarations Summary */}
                    <Card sx={{ mb: 3 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Investment Declarations Summary
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: "info.light" }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Gross Annual Salary
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {formatCurrency(taxSummary.grossAnnual)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: "success.light" }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Annual Tax ({taxSummary.regime})
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {formatCurrency(taxSummary.annualTax)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 4 }}>
                                    <Paper elevation={0} sx={{ p: 2, bgcolor: "warning.light" }}>
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Monthly TDS
                                        </Typography>
                                        <Typography variant="h5" fontWeight="bold">
                                            {formatCurrency(taxSummary.monthlyTDS)}
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Detailed Breakdown */}
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Tax Calculation Breakdown
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Component</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell>Annual Gross Salary</TableCell>
                                            <TableCell align="right">{formatCurrency(taxSummary.grossAnnual)}</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: "grey.100" }}>
                                            <TableCell colSpan={2}>
                                                <Typography variant="subtitle2" fontWeight="bold">Deductions / Cuttings</Typography>
                                            </TableCell>
                                        </TableRow>
                                        {taxSummary.deductions && (
                                            <>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>HRA Exemption</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.hraExemption)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>Section 80C Deductions</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.section80C)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>Section 80D Deductions</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.section80D)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>Home Loan Interest</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.homeLoanInterest)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>NPS (80CCD(1B))</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.NPS)}</TableCell>
                                                </TableRow>
                                                <TableRow>
                                                    <TableCell sx={{ pl: 4 }}>Other Income</TableCell>
                                                    <TableCell align="right">{formatCurrency(taxSummary.deductions.otherDeductions)}</TableCell>
                                                </TableRow>
                                                <TableRow sx={{ bgcolor: "action.hover" }}>
                                                    <TableCell><strong>Total Deductions (Total Cuttings)</strong></TableCell>
                                                    <TableCell align="right">
                                                        <strong>
                                                            {formatCurrency(
                                                                (taxSummary.grossAnnual || 0) - (taxSummary.taxableIncome || 0)
                                                            )}
                                                        </strong>
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                        <TableRow sx={{ bgcolor: "action.hover" }}>
                                            <TableCell><strong>Taxable Income</strong></TableCell>
                                            <TableCell align="right"><strong>{formatCurrency(taxSummary.taxableIncome)}</strong></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Income Tax (Base Tax)</TableCell>
                                            <TableCell align="right">{formatCurrency(taxSummary.baseTax)}</TableCell>
                                        </TableRow>
                                        {Number(taxSummary.surcharge || 0) > 0 && (
                                            <TableRow sx={{ bgcolor: "#fff8e1" }}>
                                                <TableCell>Surcharge</TableCell>
                                                <TableCell align="right">{formatCurrency(taxSummary.surcharge)}</TableCell>
                                            </TableRow>
                                        )}
                                        {Number(taxSummary.marginalRelief || 0) > 0 && (
                                            <TableRow sx={{ bgcolor: "#e8f5e9" }}>
                                                <TableCell>Marginal Relief</TableCell>
                                                <TableCell align="right" sx={{ color: "green" }}>-{formatCurrency(taxSummary.marginalRelief)}</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell>Health &amp; Education Cess (4%)</TableCell>
                                            <TableCell align="right">{formatCurrency(taxSummary.cess)}</TableCell>
                                        </TableRow>
                                        <TableRow sx={{ bgcolor: "action.selected" }}>
                                            <TableCell><strong>Total Annual Tax</strong></TableCell>
                                            <TableCell align="right"><strong>{formatCurrency(taxSummary.annualTax)}</strong></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>Monthly TDS</TableCell>
                                            <TableCell align="right"><strong>{formatCurrency(taxSummary.monthlyTDS)}</strong></TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </>
            ) : (
                <Alert severity="info">
                    No tax summary available for the selected financial year. Please submit your IT declarations first.
                </Alert>
            )}
        </Box>
    );
};

export default EmployeeTaxSummary;